import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import type { EstadoTaquilla, Evento, Pedido, PedidoCreado, Presupuesto, ResumenVentas } from '../../api/ops/pedidos';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { usePaisDelNegocio } from './pais';
import { Campo } from '../../componentes/Campo';
import { Deshacer } from '../../componentes/Deshacer';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { EntradasNoContratado } from '../pedidos/NoContratado';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { Segmentos } from '../../componentes/Segmentos';
import { rolesDe } from '../../logica/acceso';
import { dinero } from '../../logica/dinero';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import { AYUDA, FORMA_PAGO, PERMISOS, colorDeTipo, cuandoEvento, lineasDe, nombreComprador, opsEntradas } from '../pedidos/textos';
import { RUTA_TABLET } from '../../rutas';
import type { PropsPantalla } from '../tipos';
import {
    anadir,
    ayudaDelMovil,
    camposDeNombre,
    enCarrito,
    entregasPosibles,
    erroresDeVenta,
    estadoSinConexion,
    hayErrores,
    importeSinConexion,
    lineaDelCupo,
    lineaSinConexion,
    lineasDeVenta,
    lineasSinConexion,
    quedan,
    quitar,
    resumenCarrito,
    sePuedeAnadir,
    textoSinConexion,
    textoSinContacto,
    TITULO_SIN_CONTACTO,
    textoVendido,
    textoVendidoSinConexion,
    venta,
    type Carrito,
    type Entrega,
    type FormaCobro,
    type TaquillaSinConexion,
} from './venta';
import './taquilla.css';

/**
 * Taquilla (10.7, entrada `taquilla`): venta en la puerta el día del evento (G1). El evento de hoy (selector si hay
 * varios, G18), «Puedes vender N entradas» y el cupo de taquilla, baldosas por tipo con su color (G786; agotado, «no
 * quedan»), nombres opcionales en los nominativos (G3: sin nombre, «Taquilla · HH:MM»), efectivo o datáfono (solo se
 * anota la forma de pago, G5), cómo recibe las entradas y «Cobrado · importe» con el importe de la API
 * (`quoteTicketOrder`). La venta sale a los 5 s salvo «Deshacer»; después, «Vendido» y «Nueva venta».
 *
 * Modo tablet (`?modo=tablet`, también desde la puerta 6.1 con `&origen=tablet`): negro a pantalla completa con Vender ·
 * Últimas ventas (`listTicketOrders` con `channel=box_office`); en un móvil en vertical pide girarlo («Seguir en
 * vertical», `&vertical=1`). **Sin conexión** (G2, G4, G509), en la tablet con la copia de la puerta de este evento
 * (`sinConexion`): solo la parte del cupo de esta tablet («Quedan N entradas de las 15 de esta tablet · 2 ventas sin
 * enviar»), los tipos y precios de la copia, el importe con esos precios y la venta guardada en la tablet, que va sola en el
 * lote de la puerta al volver la conexión, también «Al móvil» o «Por email» (G970: las entradas se envían entonces); con su parte agotada no deja vender. Sin copia de este evento, sin conexión no
 * se vende.
 */
const FORMAS: readonly { valor: FormaCobro; texto: string; icono: NombreIcono }[] = [
    { valor: 'cash', texto: 'Efectivo', icono: 'banknote' },
    { valor: 'card_terminal', texto: 'Datáfono', icono: 'credit-card' },
];
const ENTREGAS: readonly { valor: Entrega; texto: string; sub: string; icono: NombreIcono }[] = [
    { valor: 'ya', texto: 'Entra ya', sub: 'Llegada aquí', icono: 'door-open' },
    { valor: 'movil', texto: 'Al móvil', sub: 'Por WhatsApp', icono: 'message-circle' },
    { valor: 'email', texto: 'Por email', sub: 'Con el QR', icono: 'mail' },
];

/** Lo que la Taquilla usa del evento (con la copia de la puerta, sin conexión, solo se sabe su nombre). */
type EventoTaquilla = Pick<Evento, 'id' | 'name' | 'nominative'> & { starts_at?: string };

const cuando = (e: EventoTaquilla | undefined) => (e?.starts_at === undefined ? '' : cuandoEvento({ starts_at: e.starts_at }));

function finDelDia(ahora: Date): string {
    return new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), 23, 59, 59).toISOString();
}

function useConexion(): boolean {
    const [en, setEn] = useState(() => typeof navigator === 'undefined' || navigator.onLine);
    useEffect(() => {
        const si = () => { setEn(true); };
        const no = () => { setEn(false); };
        window.addEventListener('online', si);
        window.addEventListener('offline', no);
        return () => {
            window.removeEventListener('online', si);
            window.removeEventListener('offline', no);
        };
    }, []);
    return en;
}

function useVertical(): boolean {
    const consulta = '(orientation: portrait)';
    const [v, setV] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(consulta).matches);
    useEffect(() => {
        if (typeof window.matchMedia !== 'function') {
            return;
        }
        const m = window.matchMedia(consulta);
        const cambia = () => { setV(m.matches); };
        m.addEventListener('change', cambia);
        return () => { m.removeEventListener('change', cambia); };
    }, []);
    return v;
}

export function Taquilla(props: PropsPantalla & { sinConexion?: TaquillaSinConexion }) {
    const { negocio, movil, membresia, nombreNegocio, navegar, busqueda, ruta, soloLectura, sinConexion } = props;
    const ops = opsEntradas(props.ops);
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const conexionDelNavegador = useConexion();
    const conexion = sinConexion === undefined ? conexionDelNavegador : sinConexion.conConexion;
    // Sin conexión, con la copia de la puerta de este evento (o sin evento pedido): se vende de la parte de esta tablet.
    const pedido = busqueda.get('evento');
    const caja = !conexion && sinConexion?.caja != null && (pedido === null || pedido === sinConexion.caja.evento) ? sinConexion.caja : null;
    const conCaja = caja !== null;
    const vertical = useVertical();
    const puede = permisos.puede(PERMISOS.vender);
    const modoTablet = busqueda.get('modo') === 'tablet';
    const desdePuerta = busqueda.get('origen') === 'tablet';
    const seguirVertical = busqueda.get('vertical') === '1';
    // G899: «Entra ya» solo con la sesión de la tablet emparejada (la taquilla abierta dentro de `/tablet`).
    const enLaTablet = ruta.startsWith(`${RUTA_TABLET}/`);
    const entregas = ENTREGAS.filter((x) => entregasPosibles(enLaTablet, caja !== null).includes(x.valor));
    // G975: el móvil escrito sin prefijo lleva el del país del negocio, con y sin conexión; si la tablet nunca leyó
    // `getBusiness`, el de la copia de la puerta (G986).
    const pais = usePaisDelNegocio(props.ops, negocio, conexion) ?? sinConexion?.pais ?? null;

    const [eventos, setEventos] = useState<{ lista: readonly Evento[] | null; error: unknown }>({ lista: null, error: null });
    const [elegido, setElegido] = useState<string | null>(busqueda.get('evento'));
    const [estado, setEstado] = useState<{ evento: string; datos: EstadoTaquilla | null; error: unknown } | null>(null);
    const [intento, setIntento] = useState(0);
    const [vista, setVista] = useState(0);
    const [carrito, setCarrito] = useState<Carrito>({});
    const [forma, setForma] = useState<FormaCobro | null>(null);
    const [entrega, setEntrega] = useState<Entrega | null>(null);
    const [contacto, setContacto] = useState('');
    const [nombres, setNombres] = useState<Readonly<Record<string, string>>>({});
    const [intentado, setIntentado] = useState(false);
    const [presupuesto, setPresupuesto] = useState<{ clave: string; p: Presupuesto | null } | null>(null);
    const [cobrando, setCobrando] = useState(false);
    const [vendido, setVendido] = useState<{ creado: PedidoCreado | null; entradas: number; cobrado: string; entrega: Entrega; contacto: string; nombres: string[] } | null>(null);
    const recargar = useCallback(() => { setIntento((i) => i + 1); }, []);

    useEffect(() => {
        if (!puede || conCaja) {
            return;
        }
        let vivo = true;
        const ahora = new Date();
        ops.listEvents(negocio, { status: ['published'], from: ahora.toISOString(), to: finDelDia(ahora), limit: 20 })
            .then((l) => { if (vivo) { setEventos({ lista: l.data, error: null }); } })
            .catch((e: unknown) => { if (vivo) { setEventos({ lista: null, error: e }); } });
        return () => { vivo = false; };
        // Sin conexión con la copia no se pide nada; al volver, se vuelve a leer.
    }, [ops, negocio, puede, intento, conCaja]);

    const evDeLaLista: EventoTaquilla | undefined = eventos.lista?.find((e) => e.id === elegido) ?? eventos.lista?.[0];
    const ev: EventoTaquilla | undefined = caja === null ? evDeLaLista : { id: caja.evento, name: caja.nombre, nominative: caja.articulos.some((a) => a.nominative), ...(evDeLaLista?.id === caja.evento && evDeLaLista.starts_at !== undefined ? { starts_at: evDeLaLista.starts_at } : {}) };
    const idEvento = ev?.id ?? null;

    useEffect(() => {
        if (!puede || idEvento === null || conCaja) {
            return;
        }
        let vivo = true;
        ops.getBoxOfficeStatus(negocio, idEvento)
            .then((d) => { if (vivo) { setEstado({ evento: idEvento, datos: d, error: null }); } })
            .catch((e: unknown) => { if (vivo) { setEstado({ evento: idEvento, datos: null, error: e }); } });
        return () => { vivo = false; };
    }, [ops, negocio, puede, idEvento, intento, conCaja]);

    // El importe de lo elegido lo calcula la API.
    const clavePresupuesto = `${idEvento ?? ''}|${JSON.stringify(carrito)}`;
    useEffect(() => {
        const lineas = Object.entries(carrito).map(([t, n]) => ({ ticket_type_id: t, quantity: n }));
        if (idEvento === null || lineas.length === 0 || conCaja) {
            return;
        }
        const clave = `${idEvento}|${JSON.stringify(carrito)}`;
        let vivo = true;
        ops.quoteTicketOrder(negocio, idEvento, lineas)
            .then((p) => { if (vivo) { setPresupuesto({ clave, p }); } })
            .catch(() => { if (vivo) { setPresupuesto({ clave, p: null }); } });
        return () => { vivo = false; };
    }, [ops, negocio, idEvento, carrito, conCaja]);

    useCabecera({ ayuda: puede && !modoTablet ? AYUDA.taquilla : [], acciones: [] });

    const sinPermiso = <EstadoSinPermiso seccion="Taquilla" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    const envolver = (contenido: ReactNode) => (modoTablet ? <ModoTablet desdePuerta={desdePuerta} enLaTablet={enLaTablet} navegar={navegar} ruta={ruta} ev={ev}>{contenido}</ModoTablet> : <div className="rv-tq">{contenido}</div>);

    if (!puede) {
        return envolver(sinPermiso);
    }
    if (modoTablet && movil && vertical && !seguirVertical) {
        const p = new URLSearchParams(busqueda);
        p.set('vertical', '1');
        return envolver(
            <div className="rv-tq-girar">
                <Icono nombre="smartphone" />
                <strong>Gira el móvil para vender</strong>
                <button type="button" className="rv-b rv-b-sec" onClick={() => { navegar(`${ruta}?${p.toString()}`, true); }}><span>Seguir en vertical</span></button>
            </div>,
        );
    }
    if (caja === null && eventos.error !== null) {
        return envolver(errorDePagina(eventos.error, sinPermiso, props, recargar));
    }
    if (caja === null && eventos.lista === null) {
        return envolver(<EstadoCargando seccion="la taquilla" />);
    }
    if (ev === undefined) {
        return envolver(
            <div className="rv-tq-vacia">
                <span className="rv-ca-vacia__baldosa"><Icono nombre="ticket" /></span>
                <strong>Hoy no hay venta en taquilla.</strong>
                <span>Solo se vende en la puerta el día del evento, de los tipos de entrada que lo permiten.</span>
            </div>,
        );
    }
    const e = caja !== null ? { evento: ev.id, datos: estadoSinConexion(caja), error: null } : estado?.evento === ev.id ? estado : null;
    if (e === null) {
        return envolver(<EstadoCargando seccion="la taquilla" />);
    }
    if (e.datos === null) {
        return envolver(errorDePagina(e.error, sinPermiso, props, recargar));
    }
    const datos = e.datos;
    const tipos = datos.sellable;
    const nominativo = ev.nominative === true;

    const nuevaVenta = () => {
        setVendido(null);
        setCarrito({});
        setForma(null);
        setEntrega(null);
        setContacto('');
        setNombres({});
        setIntentado(false);
    };

    // G974: ventas sin conexión que el servidor aceptó sin su contacto; se avisa en la venta y en «Vendido».
    const sinContacto = sinConexion?.sinContacto ?? 0;
    const avisoSinContacto = sinContacto > 0 && (
        <div className="rv-tq-franja" role="alert">
            <Icono nombre="triangle-alert" />
            <span className="rv-ca-fila__texto">
                <strong>{TITULO_SIN_CONTACTO}</strong>
                <span>{textoSinContacto(sinContacto)}</span>
            </span>
            {sinConexion?.olvidarSinContacto !== undefined && <button type="button" className="rv-b rv-b-sec" onClick={sinConexion.olvidarSinContacto}>Entendido</button>}
        </div>
    );

    if (vendido !== null) {
        const pedido = vendido.creado?.order ?? null;
        return envolver(
            <div className="rv-tq-vendido">
                <span className="rv-tq-vendido__marca"><Icono nombre="check" /></span>
                <strong className="rv-tq-vendido__t">{vendido.entrega === 'ya' ? 'Vendido: pueden pasar' : 'Vendido'}</strong>
                <span>{pedido === null ? textoVendidoSinConexion(vendido.entrega) : textoVendido(vendido.entrega, vendido.contacto)}</span>
                {avisoSinContacto}
                <div className="rv-ca-ventana-lineas rv-tq-vendido__lineas">
                    {pedido !== null && <Linea k="Pedido" v={pedido.reference} />}
                    <Linea k="Evento" v={ev.name ?? 'Evento'} />
                    <Linea k="Entradas" v={String(pedido?.tickets.length ?? vendido.entradas)} />
                    <Linea k="Cobrado" v={pedido === null ? vendido.cobrado : `${dinero(pedido.total)} · ${pedido.payment_method === 'cash' ? 'Efectivo' : 'Datáfono'}`} />
                    {vendido.nombres.length > 0 && <Linea k="Nombres" v={vendido.nombres.join(', ')} />}
                </div>
                <button type="button" className="rv-b rv-b-pri rv-tq-cobrar" onClick={nuevaVenta}><Icono nombre="plus" /><span>Nueva venta</span></button>
            </div>,
        );
    }

    const errores = erroresDeVenta(carrito, forma, entrega, contacto, caja !== null, pais);
    const p = presupuesto?.clave === clavePresupuesto && enCarrito(carrito) > 0 ? presupuesto.p : null;
    const aCobrar = enCarrito(carrito) === 0 ? dinero({ amount: 0, currency: tipos[0]?.price.currency ?? 'EUR' }) : caja !== null ? dinero(importeSinConexion(carrito, caja)) : p === null ? '…' : dinero(p.subtotal);
    // Sin conexión y sin la copia de este evento no se vende.
    const sinRed = !conexion && caja === null;
    const bloqueada = soloLectura || sinRed || cobrando || envio.pendiente !== null;
    const campos = nominativo ? camposDeNombre(carrito, tipos) : [];

    const cobrar = () => {
        setIntentado(true);
        if (hayErrores(errores) || forma === null || entrega === null || bloqueada) {
            return;
        }
        const ahora = new Date();
        const lineas = lineasDeVenta(carrito, tipos, nominativo, nombres, ahora);
        const cuerpo = venta(lineas, forma, entrega, contacto, pais);
        const datosVenta = { entrega, contacto, nombres: nominativo ? lineas.flatMap((l) => l.holder_names ?? []) : [], entradas: enCarrito(carrito),
            cobrado: `${aCobrar} · ${forma === 'cash' ? 'Efectivo' : 'Datáfono'}` };
        setCobrando(true);
        if (caja !== null && sinConexion !== undefined) {
            // Sin conexión (G2, G509): la venta se guarda en la tablet y va en el lote de la puerta al volver la conexión.
            const total = importeSinConexion(carrito, caja);
            envio.programar({
                texto: `Cobrado · ${aCobrar}. La venta se guarda en 5 segundos.`,
                donde: 'taquilla',
                enviar: () => {
                    sinConexion.vender({ lineas: lineasSinConexion(lineas, caja), forma, total, entraYa: entrega === 'ya', ...(cuerpo.contact === undefined ? {} : { contacto: cuerpo.contact }) });
                    setCobrando(false);
                    setVendido({ creado: null, ...datosVenta });
                },
                alDeshacer: () => { setCobrando(false); },
            });
            return;
        }
        envio.programar({
            texto: `Cobrado · ${aCobrar}. La venta se hace en 5 segundos.`,
            donde: 'taquilla',
            enviar: () => {
                ops.createBoxOfficeSale(negocio, ev.id, cuerpo)
                    .then((creado) => {
                        setCobrando(false);
                        setVendido({ creado, ...datosVenta });
                        recargar();
                    })
                    .catch((err: unknown) => {
                        setCobrando(false);
                        avisar({ tipo: 'error', texto: avisoBreveDeError(err) ?? (err instanceof ErrorApi && err.estado === 409 ? 'Ya no quedan tantas entradas. Revisa lo elegido.'
                            // G832 y G899: «Entra ya» valida los pases en la tablet de la puerta; sin tablet, `422 device_required`.
                            : err instanceof ErrorApi && err.codigo === 'device_required' ? '«Entra ya» se usa en la tablet de la puerta. Elige otra forma de darle las entradas.'
                                : 'No he podido hacer la venta. Prueba de nuevo.') });
                        recargar();
                    });
            },
            alDeshacer: () => { setCobrando(false); },
        });
    };

    const titulo = (
        <div className="rv-tq-evento">
            {eventos.lista != null && caja === null && (eventos.lista.length > 1 || (datos.same_day_events ?? []).length > 1) && !modoTablet ? (
                <Desplegable etiqueta="Evento" valor={ev.id} opciones={eventos.lista.map((x) => ({ valor: x.id, texto: [x.name ?? 'Evento', cuandoEvento(x)].filter((t) => t !== '').join(' · ') }))} onChange={(v) => { setElegido(v); nuevaVenta(); }} />
            ) : (
                !modoTablet && <span className="rv-ca-fila__texto"><strong>{ev.name ?? 'Evento'}</strong><span>{cuando(ev)}</span></span>
            )}
        </div>
    );

    const vender = (
        <div className={`rv-tq-venta${movil ? ' rv-tq-venta--movil' : ''}`}>
            <section className="rv-tq-izq">
                {titulo}
                {caja !== null && (
                    <div className="rv-tq-franja" role="status">
                        <Icono nombre="wifi-off" />
                        <span className="rv-ca-fila__texto">
                            <strong>Sin conexión</strong>
                            <span>{textoSinConexion(caja)}</span>
                        </span>
                    </div>
                )}
                {avisoSinContacto}
                {sinRed && (
                    <div className="rv-tq-franja" role="alert">
                        <Icono nombre="wifi-off" />
                        <span className="rv-ca-fila__texto">
                            <strong>Sin conexión</strong>
                            <span>Vender sin conexión necesita la copia de la puerta de este evento en esta tablet (se descarga en la Puerta). Vuelve a conectarte para vender.</span>
                        </span>
                    </div>
                )}
                <span className="rv-tq-quedan">
                    <span>{caja === null ? 'Puedes vender' : 'Quedan'}</span>
                    <strong>{quedan(datos)}</strong>
                    <span>{quedan(datos) === 1 ? 'entrada' : 'entradas'}</span>
                </span>
                {caja !== null ? <span className="rv-tq-gris">{lineaSinConexion(caja)}</span> : lineaDelCupo(datos) !== null && <span className="rv-tq-gris">{lineaDelCupo(datos)}</span>}
                <div className={`rv-tq-baldosas${modoTablet && !movil ? ' rv-tq-baldosas--tres' : ''}`}>
                    {tipos.map((t) => {
                        const n = carrito[t.ticket_type_id] ?? 0;
                        const agotado = t.available <= 0;
                        const lleno = !agotado && !sePuedeAnadir(carrito, t, datos);
                        const color = colorDeTipo(t.color);
                        const estilo = { '--rv-tq-color': color ?? (modoTablet ? '#4A4A4A' : 'var(--rv-ink)') } as CSSProperties;
                        return (
                            <div key={t.ticket_type_id} className={`rv-tq-baldosa${agotado ? ' rv-tq-baldosa--agotada' : ''}`} style={estilo}>
                                <button
                                    type="button"
                                    className="rv-tq-baldosa__b"
                                    disabled={agotado || lleno || bloqueada}
                                    aria-label={agotado ? `${t.name}, agotado` : `${t.name}, ${dinero(t.price)}. ${n > 0 ? `${String(n)} añadidas. ` : ''}Añadir una`}
                                    onClick={() => { setCarrito((c) => anadir(c, t, datos)); }}
                                >
                                    <strong>{t.name}</strong>
                                    <span className="rv-tq-baldosa__pie">
                                        <strong>{dinero(t.price)}</strong>
                                        <small>{agotado ? 'Agotado' : n > 0 ? `${String(n)} ${n === 1 ? 'añadida' : 'añadidas'}` : 'Toca para añadir'}</small>
                                    </span>
                                </button>
                                {n > 0 && <span className="rv-tq-baldosa__n" aria-hidden="true">{n}</span>}
                                {n > 0 && (
                                    <button type="button" className="rv-tq-baldosa__menos" aria-label={`Quitar una ${t.name}`} disabled={bloqueada} onClick={() => { setCarrito((c) => quitar(c, t.ticket_type_id)); }}>
                                        <Icono nombre="minus" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
                {tipos.length === 0 && <span className="rv-tq-gris">Ningún tipo de entrada de este evento se vende en taquilla.</span>}
            </section>
            <section className="rv-tq-der" aria-label="Cobrar">
                <span className="rv-tq-cobro">
                    <span className="rv-ca-fila__texto"><strong>A cobrar</strong><span>{resumenCarrito(carrito, tipos)}</span></span>
                    <strong className="rv-tq-importe" aria-live="polite">{aCobrar}</strong>
                </span>
                {intentado && errores.entradas !== null && <span className="rv-tq-error">{errores.entradas}</span>}
                <span className="rv-tq-t">Cómo paga</span>
                <div className="rv-tq-opciones rv-tq-opciones--dos" role="radiogroup" aria-label="Cómo paga">
                    {FORMAS.map((f) => (
                        <button key={f.valor} type="button" role="radio" aria-checked={forma === f.valor} className={`rv-tq-opcion${forma === f.valor ? ' rv-tq-opcion--on' : ''}`} disabled={bloqueada} onClick={() => { setForma(f.valor); }}>
                            <Icono nombre={f.icono} />
                            <strong>{f.texto}</strong>
                        </button>
                    ))}
                </div>
                {intentado && errores.forma !== null && <span className="rv-tq-error">{errores.forma}</span>}
                <span className="rv-tq-t">Cómo recibe las entradas</span>
                <div className="rv-tq-opciones" role="radiogroup" aria-label="Cómo recibe las entradas">
                    {entregas.map((x) => (
                        <button key={x.valor} type="button" role="radio" aria-checked={entrega === x.valor} className={`rv-tq-opcion${entrega === x.valor ? ' rv-tq-opcion--on' : ''}`} disabled={bloqueada} onClick={() => { setEntrega(x.valor); setContacto(''); }}>
                            <Icono nombre={x.icono} />
                            <strong>{x.texto}</strong>
                            <small>{x.sub}</small>
                        </button>
                    ))}
                </div>
                {intentado && errores.entrega !== null && <span className="rv-tq-error">{errores.entrega}</span>}
                {entrega === 'movil' && <Campo etiqueta="Móvil del comprador" tipo="tel" modo="tel" autocompletar="off" valor={contacto} onChange={setContacto} error={intentado ? errores.contacto : null} ayuda={ayudaDelMovil(caja !== null)} />}
                {entrega === 'email' && <Campo etiqueta="Email del comprador" tipo="email" autocompletar="off" valor={contacto} onChange={setContacto} error={intentado ? errores.contacto : null} ayuda="Le llegan por email, con el QR." />}
                {campos.length > 0 && (
                    <div className="rv-tq-nombres">
                        <span className="rv-tq-t">Nombres</span>
                        {campos.map((c) => (
                            <Campo key={c.clave} etiqueta={c.etiqueta} valor={nombres[c.clave] ?? ''} onChange={(v) => { setNombres((x) => ({ ...x, [c.clave]: v })); }} mayusculas="words" max={120} autocompletar="off" />
                        ))}
                        <span className="rv-tq-gris">{`Opcionales: sin nombre, la entrada sale como «Taquilla · HH:MM». Puedes añadirlo luego en el pedido.`}</span>
                    </div>
                )}
                {envio.pendiente !== null ? (
                    <div className="rv-ca-hecho" role="status">
                        <span className="rv-ca-hecho__baldosa"><Icono nombre="check" /></span>
                        <span style={{ flex: 1 }}>{envio.pendiente.texto}</span>
                        <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                    </div>
                ) : (
                    <button type="button" className="rv-b rv-b-pri rv-tq-cobrar" disabled={bloqueada} aria-busy={cobrando} onClick={cobrar}>
                        <Icono nombre={cobrando ? 'loader-circle' : 'banknote'} />
                        <span>{`Cobrado · ${aCobrar}`}</span>
                    </button>
                )}
                {soloLectura && <p className="rv-ca-nota"><Icono nombre="eye" /><span>El negocio está en solo lectura: ahora no se puede vender.</span></p>}
            </section>
        </div>
    );

    if (!modoTablet) {
        return envolver(vender);
    }
    return envolver(
        <>
            <div className="rv-tq-barra">
                <Segmentos etiqueta="Taquilla" movil={movil} items={[{ etiqueta: 'Vender', icono: 'banknote' }, { etiqueta: 'Últimas ventas', icono: 'history' }]} valor={vista} onCambio={setVista} />
            </div>
            {vista === 0 ? vender : <UltimasVentas {...props} evento={ev} />}
        </>,
    );
}

function errorDePagina(error: unknown, sinPermiso: ReactNode, props: PropsPantalla, reintentar: () => void) {
    const e = estadoDeError(error);
    if (e.tipo === 'sin-permiso') {
        return sinPermiso;
    }
    if (e.tipo === 'no-contratado') {
        return <EntradasNoContratado propietario={props.membresia?.is_owner === true} navegar={props.navegar} movil={props.movil} />;
    }
    return <EstadoError seccion="la taquilla" codigoSoporte={e.codigoSoporte} onReintentar={reintentar} movil={props.movil} />;
}

/**
 * Negro a pantalla completa, con el evento arriba y la vuelta a la puerta, al inicio de la tablet (G935: dentro de `/tablet`
 * sin venir de la puerta) o al panel.
 */
function ModoTablet({ desdePuerta, enLaTablet, navegar, ruta, ev, children }: { desdePuerta: boolean; enLaTablet: boolean; navegar: (r: string) => void; ruta: string; ev: EventoTaquilla | undefined; children: ReactNode }) {
    const salida = desdePuerta ? 'Volver a la puerta' : enLaTablet ? 'Volver al inicio' : 'Salir del modo tablet';
    return (
        <div className="rv-tq rv-tq--tablet">
            <div className="rv-tq-tablet-cab">
                <span className="rv-ca-fila__texto">
                    <strong>{ev?.name ?? 'Taquilla'}</strong>
                    <span>{cuando(ev)}</span>
                </span>
                <button type="button" className="rv-b rv-b-sec" onClick={() => { navegar(desdePuerta ? '/puerta' : ruta); }}>
                    <Icono nombre={desdePuerta || enLaTablet ? 'arrow-left' : 'x'} />
                    <span>{salida}</span>
                </button>
            </div>
            {children}
        </div>
    );
}

/**
 * Últimas ventas de la taquilla de este evento (`listTicketOrders` con `channel=box_office`), sin totales de la lista
 * (G121), y arriba el **total de la noche** en taquilla de `getEventSalesSummary` (`box_office`, G832; con
 * `entradas.informes.ver`): el dinero sale de la API, el panel no suma.
 */
function UltimasVentas(props: PropsPantalla & { evento: EventoTaquilla }) {
    const { negocio, evento } = props;
    const ops = opsEntradas(props.ops);
    const permisos = usePermisos();
    const [lista, setLista] = useState<{ datos: readonly Pedido[] | null; error: unknown } | null>(null);
    const [miembros, setMiembros] = useState<Readonly<Record<string, string>>>({});
    const [intento, setIntento] = useState(0);
    const verMiembros = permisos.puede(PERMISOS.miembros);
    const verTotal = permisos.puede(PERMISOS.informes);
    const [total, setTotal] = useState<ResumenVentas['box_office'] | null>(null);

    useEffect(() => {
        let vivo = true;
        if (verTotal) {
            ops.getEventSalesSummary(negocio, evento.id).then((r) => { if (vivo) { setTotal(r.box_office ?? null); } }).catch(() => undefined);
        }
        ops.listTicketOrders(negocio, { event_id: evento.id, channel: 'box_office', sort: '-created_at', limit: 20 })
            .then((l) => { if (vivo) { setLista({ datos: l.data, error: null }); } })
            .catch((e: unknown) => { if (vivo) { setLista({ datos: null, error: e }); } });
        if (verMiembros) {
            ops.listMembers(negocio, { limit: 100 })
                .then((l) => { if (vivo) { setMiembros(Object.fromEntries(l.data.map((m) => [m.id, m.display_name]))); } })
                .catch(() => undefined);
        }
        return () => { vivo = false; };
    }, [ops, negocio, evento.id, verMiembros, verTotal, intento]);

    if (lista === null) {
        return <EstadoCargando seccion="las últimas ventas" />;
    }
    if (lista.datos === null) {
        return <EstadoError seccion="las últimas ventas" codigoSoporte={estadoDeError(lista.error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={props.movil} />;
    }
    if (lista.datos.length === 0) {
        return (
            <div className="rv-tq-vacia">
                <strong>Todavía no hay ventas en taquilla.</strong>
                <span>{`Las de ${evento.name ?? 'este evento'} aparecen aquí al cobrarlas.`}</span>
            </div>
        );
    }
    const formas = total?.by_payment_method;
    return (
        <>
            {total != null && (
                <div className="rv-tq-total" aria-label="Total de la noche">
                    <span className="rv-ca-fila__texto">
                        <strong>Total de la noche</strong>
                        <span>{[`${String(total.tickets)} ${total.tickets === 1 ? 'entrada' : 'entradas'}`, formas?.cash == null ? '' : `Efectivo ${dinero(formas.cash)}`,
                            formas?.card_terminal == null ? '' : `Datáfono ${dinero(formas.card_terminal)}`].filter((x) => x !== '').join(' · ')}</span>
                    </span>
                    <strong className="rv-tq-ultima__importe">{dinero(total.total)}</strong>
                </div>
            )}
        <ul className="rv-tq-ultimas" aria-label="Últimas ventas">
            {lista.datos.map((p) => {
                const quien = p.created_by_member_id == null ? '' : miembros[p.created_by_member_id] ?? '';
                const comprador = p.customer_id == null ? (p.tickets.some((t) => t.arrived_at != null) ? 'Entraron al momento' : 'Sin datos') : nombreComprador(p, {});
                const forma = p.payment_method === 'cash' || p.payment_method === 'card_terminal' ? FORMA_PAGO[p.payment_method] : null;
                return (
                    <li key={p.id} className="rv-tq-ultima">
                        <strong className="rv-tq-ultima__hora">{new Date(p.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</strong>
                        <span className="rv-ca-fila__texto">
                            <strong>{lineasDe(p)}</strong>
                            <span>{[comprador === 'Cliente' ? '' : comprador, quien === '' ? '' : `vendió ${quien}`, p.reference].filter((x) => x !== '').join(' · ')}</span>
                        </span>
                        <span className="rv-tq-ultima__importe">
                            <strong>{dinero(p.total)}</strong>
                            {forma !== null && <small><Icono nombre={forma[1]} />{p.payment_method === 'cash' ? 'Efectivo' : 'Datáfono'}</small>}
                        </span>
                    </li>
                );
            })}
        </ul>
        </>
    );
}

function Linea({ k, v }: { k: string; v: string }) {
    return (
        <div className="rv-ca-ventana-linea">
            <span>{k}</span>
            <strong>{v}</strong>
        </div>
    );
}
